import { useState, useRef, useMemo, useCallback, useEffect, type ReactNode } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { Sparkles, XClose } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { LoadingSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton'
import { Button } from '@/components/button/Button'
import { computeDiff, groupDiffSegments, extractChangedSection } from '@/inline-edit/util/compute-diff'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationLongMs } from '@/constants/constants-ui'
import { useAppDispatch } from '@/store/store-hooks'
import { kDiffCardGap, kDiffCardMinLeft } from '@/inline-edit/constants/diff-card'

// ── Types ──

type DiffReviewViewProps = {
  oldMarkdown: string
  newMarkdown: string
  onAccept: (resolvedMarkdown: string) => void
  onReject: () => void
  /** Insert the accepted text below the original without modifying it */
  onInsertBelow: (insertedText: string) => void
  /** Ref to the editor container — used to match card width and position */
  editorContainerRef: React.RefObject<HTMLDivElement | null>
  /** When true, shows a loading skeleton instead of the diff content */
  isLoading?: boolean
  /** Called when user cancels during loading */
  onCancelEdit?: () => void
  /** Optional transform applied to display markdown before rendering (e.g., converting citation markers to badges) */
  preprocessDisplay?: (markdown: string) => string
}

// ── Pure helpers ──

/**
 * Prepares text for use inside `<del>`/`<ins>` HTML tags.
 *
 * Markdown block syntax (headings, HRs) won't render inside inline HTML elements,
 * so we convert them to their HTML equivalents. Inline syntax (bold, italic) works
 * natively inside HTML elements and is left as-is.
 *
 * For whitespace-only text (indentation changes), escapes HTML and uses &nbsp; so
 * the deleted/inserted space renders as a visible colored block instead of collapsing.
 */
function prepareForDiffTag(text: string): string {
  if (text.trim() === '') {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/ /g, '&nbsp;')
  }
  return text
    .replace(/^#{6}\s+(.+)$/gm, '<h6>$1</h6>')
    .replace(/^#{5}\s+(.+)$/gm, '<h5>$1</h5>')
    .replace(/^#{4}\s+(.+)$/gm, '<h4>$1</h4>')
    .replace(/^#{3}\s+(.+)$/gm, '<h3>$1</h3>')
    .replace(/^#{2}\s+(.+)$/gm, '<h2>$1</h2>')
    .replace(/^#{1}\s+(.+)$/gm, '<h1>$1</h1>')
    .replace(/^---$/gm, '<hr />')
}

/** Regex matching a markdown list prefix: `*   `, `-   `, or `1.  ` with optional leading whitespace. */
const LIST_PREFIX_RE = /^(\s*(?:[*-]|\d+\.)\s+)/

/**
 * Wraps text in an HTML diff tag (`<del>` or `<ins>`), splitting at paragraph breaks
 * so tags stay inline within paragraphs. Handles block-level markdown (headings, list items)
 * by placing the diff tag inside the block structure rather than wrapping it.
 */
function wrapTag(tag: string, text: string): string {
  const paragraphs = text.split(/(\n\n+)/)
  return paragraphs
    .map((part) => {
      if (/^\n\n+$/.test(part)) return part
      if (!part) return ''

      const headingMatch = part.match(/^(#{1,6})\s+(.+)$/)
      if (headingMatch) {
        const level = headingMatch[1].length
        return `<h${level}><${tag}>${headingMatch[2]}</${tag}></h${level}>`
      }

      const listMatch = part.match(LIST_PREFIX_RE)
      if (listMatch) {
        return `${listMatch[1]}<${tag}>${part.slice(listMatch[1].length)}</${tag}>`
      }

      return `<${tag}>${prepareForDiffTag(part)}</${tag}>`
    })
    .join('')
}

// ── Main component ──

/**
 * Floating card shown during an inline edit. Positions itself to the left of the
 * artifact editor so users can see the suggestion and original document side by side.
 *
 * Three states:
 * - Loading — skeleton body + Cancel button while the BE response is in flight.
 * - No changes — confirmation text + Done button when the AI returned no edits.
 * - Diff — redlines for the changed section + Replace / Insert Below buttons.
 */
export default function DiffReviewView({
  oldMarkdown,
  newMarkdown,
  onAccept,
  onReject,
  onInsertBelow,
  editorContainerRef,
  isLoading,
  onCancelEdit,
  preprocessDisplay,
}: DiffReviewViewProps) {
  // ── State ──
  const appDispatch = useAppDispatch()
  const cardRef = useRef<HTMLDivElement>(null)

  // ── Derived values ──
  const changeGroups = useMemo(() => {
    try {
      return groupDiffSegments(computeDiff(oldMarkdown, newMarkdown))
    } catch (error) {
      console.error('Failed to compute diff:', error)
      appDispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'Unable to display changes. Applying edit directly.',
          durationMs: kToastDurationLongMs,
        })
      )
      onAccept(newMarkdown)
      return []
    }
  }, [oldMarkdown, newMarkdown, appDispatch, onAccept])

  const displayGroups = useMemo(() => extractChangedSection(changeGroups), [changeGroups])
  const hasChanges = changeGroups.some((g) => g.type === 'change')

  // ── Callbacks ──

  const handleAccept = useCallback(() => {
    // Accept all changes — build the full document with all inserts applied
    const resolved = changeGroups.map((g) => (g.type === 'equal' ? g.text : g.inserted)).join('')
    onAccept(resolved)
  }, [changeGroups, onAccept])

  const handleInsertBelow = useCallback(() => {
    // Build the changed section with all diffs accepted for insertion below the original
    const textToInsert = displayGroups.map((g) => (g.type === 'equal' ? g.text : g.inserted || '')).join('')
    onInsertBelow(textToInsert)
  }, [displayGroups, onInsertBelow])

  // ── Effects ──

  // Escape key — cancels during loading, dismisses after loading
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (isLoading) {
          onCancelEdit?.()
        } else {
          onReject()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isLoading, onReject, onCancelEdit])

  // Click outside the diff card — after loading, dismisses immediately. During loading, do nothing.
  useEffect(() => {
    if (isLoading) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Element
      if (target.closest('[data-inline-edit]') || target.closest('[data-radix-portal]')) return
      // Also check the card's visible bounds — the card element extends beyond its
      // visible content due to maxHeight, so DOM containment alone isn't reliable
      const card = cardRef.current
      if (card) {
        const rect = card.getBoundingClientRect()
        if (e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom) return
      }
      onReject()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isLoading, onReject])

  // ── Memos ──

  const displayMarkdown = useMemo(() => {
    const parts: string[] = []
    let pendingListPrefix = ''

    for (let idx = 0; idx < displayGroups.length; idx++) {
      const g = displayGroups[idx]

      if (g.type === 'equal') {
        if (pendingListPrefix && g.text.startsWith(pendingListPrefix)) {
          parts.push(g.text.slice(pendingListPrefix.length))
        } else {
          parts.push(g.text)
        }
        pendingListPrefix = ''
        continue
      }

      // Whitespace-only changes before list items: emit the list marker first
      if (g.deleted.trim() === '' && g.inserted.trim() === '') {
        const next = displayGroups[idx + 1]
        const prefix = next?.type === 'equal' ? next.text.match(LIST_PREFIX_RE)?.[1] : undefined
        if (prefix) {
          pendingListPrefix = prefix
          parts.push(prefix)
          if (g.deleted) parts.push(`<del>${prepareForDiffTag(g.deleted)}</del>`)
          if (g.inserted) parts.push(`<ins>${prepareForDiffTag(g.inserted)}</ins>`)
          continue
        }
      }

      // Single-paragraph paired change
      const hasParaBreaks = g.deleted?.includes('\n\n') || g.inserted?.includes('\n\n')
      if (g.deleted && g.inserted && !hasParaBreaks) {
        parts.push(`<del>${prepareForDiffTag(g.deleted)}</del><ins>${prepareForDiffTag(g.inserted)}</ins>`)
        continue
      }

      // Multi-paragraph or single-sided
      const del = g.deleted ? wrapTag('del', g.deleted) : ''
      const ins = g.inserted ? wrapTag('ins', g.inserted) : ''
      parts.push(del + ins)
    }

    return parts.join('')
  }, [displayGroups])

  const markdownComponents = useMemo(
    () => ({
      del: ({ children }: { children?: ReactNode }) => (
        <del className="text-brand-neutral-500 [&_strong]:text-inherit [&_em]:text-inherit" style={{ textDecoration: 'line-through' }}>
          {children}
        </del>
      ),
      ins: ({ children }: { children?: ReactNode }) => (
        <ins
          className="text-brand-700 font-bold bg-brand-100 no-underline [&_strong]:text-inherit [&_em]:text-inherit"
          style={{ boxShadow: '0 4.5px 0 #D8E9FE, 0 -4.5px 0 #D8E9FE' }}
        >
          {children}
        </ins>
      ),
    }),
    []
  )

  // ── Positioning ──

  const [cardStyle, setCardStyle] = useState<React.CSSProperties>({ position: 'fixed', zIndex: 50, opacity: 0 })
  const [isOverlay, setIsOverlay] = useState(false)

  useEffect(() => {
    const updatePosition = () => {
      const el = editorContainerRef.current
      if (!el) return
      let scrollViewport: HTMLElement | null = el.parentElement
      while (scrollViewport && getComputedStyle(scrollViewport).overflowY !== 'auto') {
        scrollViewport = scrollViewport.parentElement
      }
      if (!scrollViewport) return
      const viewportRect = scrollViewport.getBoundingClientRect()

      const editorWidth = el.offsetWidth
      const editorLeft = el.getBoundingClientRect().left

      const cardWidth = editorWidth
      const idealLeft = editorLeft - cardWidth - kDiffCardGap
      const hasSpaceToLeft = idealLeft >= kDiffCardMinLeft
      setIsOverlay(!hasSpaceToLeft)

      setCardStyle({
        position: 'fixed',
        left: hasSpaceToLeft ? idealLeft : editorLeft,
        top: viewportRect.top,
        width: cardWidth,
        maxHeight: viewportRect.height,
        zIndex: 50,
        opacity: 1,
      })
    }
    const raf = requestAnimationFrame(updatePosition)
    window.addEventListener('resize', updatePosition)

    // Observe the editor container for layout changes (e.g., sidebar drawer open/close)
    // which don't trigger window resize but do shift the editor position
    const observer = new ResizeObserver(updatePosition)
    if (editorContainerRef.current) observer.observe(editorContainerRef.current)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', updatePosition)
      observer.disconnect()
    }
  }, [editorContainerRef])

  // ── Render ──

  return (
    <>
      <div
        ref={cardRef}
        style={cardStyle}
        className="relative rounded-2xl bg-brand-neutral-50 shadow-[0_4px_6px_0px_rgba(0,0,0,0.1),0_2px_4px_0px_rgba(0,0,0,0.06)] flex flex-col gap-6 p-6"
        data-inline-edit
      >
        {!isLoading && isOverlay && (
          <div className="absolute top-2 right-2 z-10">
            <IconButton icon={<XClose />} aria-label="Dismiss" size="sm" variant="plain" color="neutral" onClick={onReject} />
          </div>
        )}

        {/* Contents: title + body with 16px gap */}
        <div className="flex flex-col gap-4 min-h-0">
          {/* Title */}
          <div className="flex items-center gap-2 shrink-0">
            <Sparkles className="w-6 h-6 text-brand-neutral-950 shrink-0" />
            <span className="text-lg font-bold text-brand-neutral-950">{isLoading ? 'Applying edit...' : 'Review suggested edit'}</span>
          </div>

          {/* Body */}
          {isLoading ? (
            <div className="flex flex-col gap-2">
              <LoadingSkeleton width="100%" />
              <LoadingSkeleton width="90%" />
              <LoadingSkeleton width="80%" />
              <LoadingSkeleton width="85%" />
            </div>
          ) : !hasChanges ? (
            <p className="text-base leading-6 tracking-[-0.32px] text-brand-neutral-700">No changes suggested.</p>
          ) : (
            <div className="overflow-y-auto min-h-0">
              <div className="prose max-w-none redline-diff text-brand-neutral-950">
                <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>
                  {preprocessDisplay ? preprocessDisplay(displayMarkdown) : displayMarkdown}
                </Markdown>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end shrink-0 gap-3">
          {isLoading ? (
            <Button variant="outlined" color="primary" size="md" onClick={onCancelEdit}>
              Cancel
            </Button>
          ) : !hasChanges ? (
            <Button variant="filled" color="primary" size="md" className="min-w-[80px]" onClick={() => onAccept(newMarkdown)}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="outlined" color="primary" size="md" className="min-w-[80px]" onClick={handleInsertBelow}>
                Insert Below
              </Button>
              <Button variant="filled" color="primary" size="md" className="min-w-[80px]" onClick={handleAccept}>
                Replace
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  )
}
